import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxStepsIndicator,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import { AuthContext } from '@urbanx/agx.auth.client'
import { deleteFile } from 'services'
import { AgxToastState } from 'types/commonTypes'
import { UploadedDocument } from '@urbanx/agx.campaigns.client'
import {
  buildComplianceState,
  ComplianceData,
  ComplianceKey,
  getComplianceItems,
  getFinalEnabled,
  getRequiredCompletion,
} from './contractCompliance'
import ContractComplianceStep from './ContractComplianceStep'
import ContractUploadStep from './ContractUploadStep'
import ContractVersionStep from './ContractVersionStep'

enum ContractUploadStepId {
  Upload = 1,
  Compliance = 2,
  Version = 3,
}

interface ContractUploadModalProps {
  isOpen: boolean
  showTenancyAgreement: boolean
  updateToastState: (toastState: AgxToastState) => void
  onClose: () => void
  onComplete: (payload: {
    complianceData: ComplianceData
    uploadedDocument: UploadedDocument
    isFinalSelected: boolean
  }) => Promise<void>
}

const ContractUploadModal = ({
  isOpen,
  showTenancyAgreement,
  updateToastState,
  onClose,
  onComplete,
}: ContractUploadModalProps) => {
  const { getAuthToken } = useContext(AuthContext)
  const [currentStep, setCurrentStep] = useState<ContractUploadStepId>(
    ContractUploadStepId.Upload
  )
  const [uploadedDocument, setUploadedDocument] = useState<
    UploadedDocument | undefined
  >(undefined)
  const [isUploading, setIsUploading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasCompleted, setHasCompleted] = useState(false)
  const [complianceData, setComplianceData] = useState<ComplianceData>(() =>
    buildComplianceState()
  )
  const [isFinalSelected, setIsFinalSelected] = useState(false)

  const { required, optional } = useMemo(
    () => getComplianceItems(showTenancyAgreement),
    [showTenancyAgreement]
  )

  const requiredComplete = useMemo(
    () => getRequiredCompletion(complianceData),
    [complianceData]
  )

  const finalEnabled = useMemo(
    () => getFinalEnabled(complianceData, showTenancyAgreement),
    [complianceData, showTenancyAgreement]
  )

  useEffect(() => {
    if (!finalEnabled && isFinalSelected) {
      setIsFinalSelected(false)
    }
  }, [finalEnabled, isFinalSelected])

  useEffect(() => {
    if (!isOpen) return
    setCurrentStep(ContractUploadStepId.Upload)
    setUploadedDocument(undefined)
    setIsUploading(false)
    setIsSubmitting(false)
    setHasCompleted(false)
    setComplianceData(buildComplianceState())
    setIsFinalSelected(false)
  }, [isOpen])

  const stepTitle = useMemo(() => {
    switch (currentStep) {
      case ContractUploadStepId.Upload:
        return 'Upload Contract'
      case ContractUploadStepId.Compliance:
        return 'Contract Compliant Check'
      case ContractUploadStepId.Version:
        return 'Confirm Contract Version'
      default:
        return 'Upload Contract'
    }
  }, [currentStep])

  const handleFileChanged = useCallback(
    ({ value }: { value?: UploadedDocument[] }) => {
      setUploadedDocument(value?.[0])
    },
    []
  )

  const handleComplianceToggle = useCallback(
    (key: ComplianceKey, checked: boolean) => {
      setComplianceData((prev) => ({
        ...prev,
        [key]: checked,
      }))
    },
    []
  )

  const handleClose = useCallback(async () => {
    if (uploadedDocument?.containerFilePath && !hasCompleted) {
      try {
        const authToken = await getAuthToken()
        if (authToken) {
          await deleteFile(authToken, uploadedDocument.containerFilePath)
        }
      } catch {
        updateToastState({
          color: 'error',
          message: 'Error deleting uploaded contract',
          open: true,
        })
      }
    }

    onClose()
  }, [getAuthToken, hasCompleted, onClose, updateToastState, uploadedDocument])

  const handleDialogChange = useCallback(
    (open: boolean) => {
      if (!open) {
        void handleClose()
      }
    },
    [handleClose]
  )

  const handleComplete = useCallback(async () => {
    if (!uploadedDocument) return
    setIsSubmitting(true)
    try {
      await onComplete({
        complianceData,
        uploadedDocument: uploadedDocument,
        isFinalSelected,
      })
      setHasCompleted(true)
      onClose()
    } catch {
      updateToastState({
        color: 'error',
        message: 'Error saving contract',
        open: true,
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [
    complianceData,
    isFinalSelected,
    onClose,
    onComplete,
    updateToastState,
    uploadedDocument,
  ])

  const renderStepContent = () => {
    switch (currentStep) {
      case ContractUploadStepId.Upload:
        return (
          <ContractUploadStep
            uploadedDocument={uploadedDocument}
            updateToastState={updateToastState}
            deleteFile={deleteFile}
            onFileChanged={handleFileChanged}
            onUploadingChange={setIsUploading}
          />
        )
      case ContractUploadStepId.Compliance:
        return (
          <ContractComplianceStep
            required={required}
            optional={optional}
            complianceData={complianceData}
            onToggle={handleComplianceToggle}
          />
        )
      default:
        return (
          <ContractVersionStep
            finalEnabled={finalEnabled}
            isFinalSelected={isFinalSelected}
            onSelectFinal={setIsFinalSelected}
          />
        )
    }
  }

  const renderActionButtons = () => {
    if (currentStep === ContractUploadStepId.Upload) {
      return (
        <>
          <AgxButton
            id="contractUploadCancel"
            text="Cancel"
            variant="hollow"
            size="large"
            stretch
            onClick={handleClose}
          />
          <AgxButton
            id="contractUploadNext"
            dataTestId="btn-contractNext"
            text="Next"
            variant="primary"
            size="large"
            stretch
            disabled={!uploadedDocument || isUploading}
            onClick={() => setCurrentStep(ContractUploadStepId.Compliance)}
          />
        </>
      )
    }

    if (currentStep === ContractUploadStepId.Compliance) {
      return (
        <>
          <AgxButton
            id="contractComplianceBack"
            dataTestId="btn-contractBack"
            text="Back"
            variant="hollow"
            size="large"
            stretch
            onClick={() => setCurrentStep(ContractUploadStepId.Upload)}
          />
          <AgxButton
            id="contractComplianceNext"
            dataTestId="btn-contractNext"
            text="Next"
            variant="primary"
            size="large"
            stretch
            disabled={!requiredComplete}
            onClick={() => setCurrentStep(ContractUploadStepId.Version)}
          />
        </>
      )
    }

    return (
      <>
        <AgxButton
          id="contractVersionBack"
          dataTestId="btn-contractBack"
          text="Back"
          variant="hollow"
          size="large"
          stretch
          onClick={() => setCurrentStep(ContractUploadStepId.Compliance)}
          disabled={isSubmitting}
        />
        <AgxButton
          id="contractVersionComplete"
          dataTestId="btn-contractComplete"
          text="Complete"
          variant="primary"
          size="large"
          stretch
          onClick={handleComplete}
          disabled={
            !uploadedDocument ||
            isSubmitting ||
            (!finalEnabled && isFinalSelected) ||
            !requiredComplete
          }
        />
      </>
    )
  }

  return (
    <AgxModal
      type="single"
      variant="withDivider"
      actionButtonContainerStyle="withDivider"
      minWidth="600px"
      maxWidth="600px"
      maxHeight="calc(100dvh - 250px)"
      secondaryTitle={
        <AgxSpacer
          direction="vertical"
          gap="spacing-2xl"
          fill
          contentAlignment="start"
          padding={{ bottom: 'spacing-xl' }}
        >
          <AgxSpacer
            direction="horizontal"
            gap="spacing-ml"
            fill
            contentAlignment="center"
            contentJustification="start"
          >
            <AgxStepsIndicator totalLevels={3} currentLevel={currentStep} />
          </AgxSpacer>
          <AgxTypography
            id="contractUploadTitle"
            type="body_ui_strong"
            size="medium"
            color="var(--text-primary)"
          >
            {stepTitle}
          </AgxTypography>
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          data-testid="modal-contractUpload"
          direction="vertical"
          gap="spacing-xl"
          fill
          padding={{
            top: 'spacing-3xl',
            bottom: 'spacing-3xl',
            left: 'spacing-5xl',
            right: 'spacing-5xl',
          }}
        >
          {renderStepContent()}
        </AgxSpacer>
      }
      actionButtons={
        <AgxSpacer
          direction="horizontal"
          gap="spacing-md"
          fill
          contentAlignment="center"
          contentJustification="center"
        >
          {renderActionButtons()}
        </AgxSpacer>
      }
      setIsDialogOpen={handleDialogChange}
      isOpen={isOpen}
    />
  )
}

export default ContractUploadModal
